import { useState, useCallback, useMemo } from "react";
import { useBoolean, useDisclosure } from "@chakra-ui/react";
import { IContact } from "../types/Contact";
import { IMode, ITag, TagAction } from "../types/Tag";
import { useAddTagToContact, useGetTeamTags } from "../queries/useTags";
import { ACTION } from "../../../constants";

interface UseTagManagementProps {
  teamId: number;
  contact?: IContact;
  refetchContactsData: () => void;
  onClose?: () => void;
}

interface UseTagManagementReturn {
  mode: IMode;
  checkedList: { id: number }[];
  isTagItem: boolean;
  isOpen: boolean;
  tagsList: ITag[];
  availableTags: ITag[];
  isLoading: boolean;

  handleTagsCheckboxChange: (id: number) => void;
  handleAssignTagsToContact: () => void;
  isValidAssign: boolean;

  handleOpenManageTags: () => void;
  handleCloseManageTags: () => void;
  handleAddNewTag: () => void;
  handleClose: () => void;

  handleTagClick: (tag: ITag) => void;
  resetTagManagement: () => void;

  openTagItem: () => void;
  closeTagItem: () => void;
  onOpen: () => void;
  onClose: () => void;

  refetchTags: () => void;

  title: string;
  editable: boolean;
  teamId: number;
  tagsToShow: ITag[];
  shouldShowCreateForm: boolean;
  isContactAssignmentMode: boolean;
}

export const useTagManagement = ({
  teamId,
  contact,
  refetchContactsData,
  onClose,
}: UseTagManagementProps): UseTagManagementReturn => {
  const [checkedList, setCheckedList] = useState<{ id: number }[]>([]);
  const [isTagItem, { on: openTagItem, off: closeTagItem }] = useBoolean();
  const { isOpen, onOpen, onClose: onCloseDisclosure } = useDisclosure();

  const { data: { results: tagsList = [] } = {}, refetch: refetchTags } =
    useGetTeamTags({ teamId });
  const { mutate: fireAddTagToContact, isLoading } = useAddTagToContact({
    refetchContactsData: refetchContactsData || (() => {}),
    refetchTags: refetchTags || (() => {}),
  });

  const availableTags = useMemo(() => {
    if (!contact?.tags || !tagsList) return [];

    return tagsList.filter(
      (tag: ITag) => !contact.tags.some(t => t.id === tag.id),
    );
  }, [contact?.tags, tagsList]);

  const isContactAssignmentMode = Boolean(contact);
  const editable = !isContactAssignmentMode;

  const shouldShowCreateForm = useMemo(() => {
    return isContactAssignmentMode && availableTags.length === 0;
  }, [isContactAssignmentMode, availableTags.length]);
  const [mode, setMode] = useState<IMode>({
    action: (shouldShowCreateForm ? ACTION.ADD : "") as TagAction,
  });

  const title = useMemo(() => {
    if (isTagItem || shouldShowCreateForm) {
      return mode.action === ACTION.ADD ? "Create New Tag" : "Edit Tag";
    }
    return isContactAssignmentMode ? "Assign Tags" : "Manage Tags";
  }, [isTagItem, mode.action, isContactAssignmentMode]);

  const handleTagsCheckboxChange = useCallback((id: number): void => {
    setCheckedList(prevList => {
      const index = prevList.findIndex(item => item.id === id);
      if (index === -1) {
        return [...prevList, { id }];
      } else {
        return prevList.filter(item => item.id !== id);
      }
    });
  }, []);

  const handleAssignTagsToContact = useCallback(() => {
    if (!contact?.id) return;

    const tagIds = [...(contact.tags || []), ...checkedList].map(
      item => item.id,
    );

    fireAddTagToContact({ contactId: contact.id, tagIds });
    setCheckedList([]);

    // Close the drawer when assigning tags to contact
    if (onClose) {
      onClose();
    }
  }, [contact?.id, contact?.tags, checkedList, fireAddTagToContact, onClose]);

  const handleOpenManageTags = useCallback(() => {
    onOpen();
  }, [onOpen]);

  const handleCloseManageTags = useCallback(() => {
    onCloseDisclosure();
    closeTagItem();
    setMode({ action: "" as TagAction });
  }, [onCloseDisclosure, closeTagItem]);

  const handleAddNewTag = useCallback(() => {
    setMode({ action: ACTION.ADD as TagAction });
    openTagItem();
  }, [openTagItem]);

  const handleClose = useCallback(() => {
    if (isTagItem) {
      closeTagItem();
    } else if (onClose) {
      // Close the entire drawer
      onClose();
    }
  }, [isTagItem, closeTagItem, onClose]);

  const handleTagClick = useCallback(
    (tag: ITag): void => {
      if (isContactAssignmentMode) {
        handleTagsCheckboxChange(tag.id);
      } else {
        openTagItem();
        setMode({ action: ACTION.EDIT as TagAction, tag });
      }
    },
    [checkedList, handleTagsCheckboxChange, openTagItem, setMode],
  );

  const resetTagManagement = useCallback(() => {
    setCheckedList([]);
    setMode({ action: ACTION.ADD as TagAction });
  }, []);

  const isValidAssign = checkedList.length > 0;

  const tagsToShow = useMemo(() => {
    return isContactAssignmentMode ? availableTags : tagsList;
  }, [isContactAssignmentMode, availableTags, tagsList]);

  return {
    mode,
    checkedList,
    isTagItem,
    isOpen,
    tagsList,
    availableTags,
    isLoading,

    handleTagsCheckboxChange,
    handleAssignTagsToContact,
    isValidAssign,

    handleOpenManageTags,
    handleCloseManageTags,
    handleAddNewTag,
    handleClose,

    handleTagClick,
    resetTagManagement,

    openTagItem,
    closeTagItem,
    onOpen,
    onClose: onCloseDisclosure,

    refetchTags,

    title,
    editable,
    teamId,
    tagsToShow,
    shouldShowCreateForm,
    isContactAssignmentMode,
  };
};
